<script setup lang="ts">
import { computed, ref, defineProps, defineEmits, onMounted, onUnmounted } from 'vue'
import { ElTooltip } from 'element-plus'
import { DrawPolygonIcon, VectorSquareIcon, RulerVerticalIcon } from 'Common/components/elements/icons/Icons'
import { useAnnotationsStore } from 'Store/pages/imageReview/annotationsStore'
//Types
import { Ref } from 'vue'
import { AnnotatorModes, MarkerTypes } from 'Modules/types/pages/imageReview/enums'
//Feature Flags

interface PropTypes {
  mode: AnnotatorModes
  selectedMarkerType: MarkerTypes
  showTooltipAfter: number
}

const props = defineProps<PropTypes>()
const wrapperRef: Ref<HTMLDivElement> = ref()

const emit = defineEmits<{
  change: [option: MarkerTypes]
}>()

const annotationsStore = useAnnotationsStore()
const isDropdownOpen = ref(false)
const selectableMarkerTypes = Object.values(MarkerTypes).filter((type) => type !== MarkerTypes.Selection)

const selectMarkerType = (e: MouseEvent, option: MarkerTypes) => {
  emit('change', option)
  isDropdownOpen.value = false
}

const selectorOptionButtonClass = (markerType: MarkerTypes) => {
  const showInSelectedStyling = props.mode === AnnotatorModes.Create && markerType === props.selectedMarkerType

  if (showInSelectedStyling) return 'bg-gray-600 text-white font-semibold'
  if (markerType === MarkerTypes.Measurement && measurementDisabled.value === true)
    return 'bg-gray-800 text-gray-300 hover:bg-gray-400 hover:text-white cursor-not-allowed'
  else return 'bg-gray-800 text-gray-300 hover:bg-gray-400 hover:text-white'
}

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

const autoCloseDropdown = (e) => {
  const isMenuButton = e.target === wrapperRef.value
  const containsMenuButton = wrapperRef.value?.contains(e.target)
  const shouldClose = !containsMenuButton && !isMenuButton && isDropdownOpen.value === true
  if (shouldClose) isDropdownOpen.value = false
}

const measurementDisabled = computed(() => annotationsStore.stagedAnnotations.length > 1)

onMounted(() => {
  document.addEventListener('mousedown', autoCloseDropdown)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', autoCloseDropdown)
  wrapperRef.value = null
})
</script>

<template>
  <div class="relative inline-block text-left z-20">
    <el-tooltip
      placement="bottom"
      content="Create Marker"
      :show-after="props.showTooltipAfter"
      :disabled="isDropdownOpen"
    >
      <button
        ref="wrapperRef"
        class="text-white focus:outline-none border-0 rounded-[0]"
        :class="`${props.mode === AnnotatorModes.Create ? 'bg-gray-600' : 'bg-transparent'}`"
        @click="(e) => toggleDropdown(e)"
      >
        <DrawPolygonIcon v-if="selectedMarkerType == MarkerTypes.Polygon" />
        <VectorSquareIcon v-else-if="selectedMarkerType === MarkerTypes.Rectangle" />
        <RulerVerticalIcon v-else-if="selectedMarkerType === MarkerTypes.Measurement" />
      </button>
    </el-tooltip>

    <transition name="fade">
      <div
        v-if="isDropdownOpen"
        tabindex="0"
        class="origin-top-left absolute left-[25%] mt-2 rounded-md shadow-lg bg-gray-800 bg-opacity-75"
      >
        <div class="py-1 px-1 flex flex-col gap-1">
          <button
            v-for="type in selectableMarkerTypes"
            :key="type"
            class="rounded-[0] p-2"
            :class="selectorOptionButtonClass(type)"
            :disabled="type === MarkerTypes.Measurement && measurementDisabled"
            @click="(e) => selectMarkerType(e, type)"
          >
            <span class="flex gap-2 items-center justify-center">
              <template v-if="type === MarkerTypes.Rectangle">
                <VectorSquareIcon />
                <span>Rectangle</span>
              </template>
              <template v-else-if="type === MarkerTypes.Polygon">
                <DrawPolygonIcon />
                <span>Polygon</span>
              </template>
              <template v-else-if="type === MarkerTypes.Measurement">
                <RulerVerticalIcon />
                <span>Measurement</span>
              </template>
            </span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
